import { CheckCircleIcon, XIcon, OctagonAlertIcon, InfoIcon } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@util/tailwind";

interface Props {
    type: "error" | "success" | "info" | string
    message: string;
    description?: string;
    duration?: number;
    className?: string;
    onClose?: () => void;
}

export default function NotificationMessage({
    type,
    message,
    description,
    duration = 5000,
    className,
    onClose,
}: Props) {
    const [isVisible, setIsVisible] = useState(true);
    useEffect(() => {
        if (duration > 0) {
            const timer = setTimeout(() => {
                setIsVisible(false);
                if (onClose) onClose();
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [duration, onClose]);

    const handleClose = () => {
        setIsVisible(false);
        if (onClose) onClose();
    };

    if (!isVisible) return null;

    return (
        <div
            className={cn(
                "fixed top-4 right-4 z-50 max-w-md animate-in fade-in slide-in-from-top-2 duration-300",
                className,
            )}
        >
            <div className={`rounded-lg border ${type === "success" ? "border-green-100" : type === "error" ? "border-red-100" : type === "info" ? "border-blue-100" : ""} bg-white p-4 shadow-md`}>
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                        {type === "success" ?
                            <CheckCircleIcon className="h-5 w-5 text-green-500" /> :
                            type === "error" ?
                                <OctagonAlertIcon className="h-5 w-5 text-red-500" /> :
                                type === "info" ?
                                    <InfoIcon className="h-5 w-5 text-blue-300" /> : ""}
                    </div>
                    <div className="flex-1">
                        <h3 className="font-medium text-gray-900">
                            {message}
                        </h3>
                        {description && (
                            <p className="mt-1 text-sm text-gray-500">
                                {description}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        className="ml-4 inline-flex flex-shrink-0 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                        onClick={handleClose}
                    >
                        <span className="sr-only">
                            Close
                        </span>
                        <XIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </div>
    );
}
